import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { Spin } from "antd";
import {
  GridComponent,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Filter,
  Toolbar,
  ExcelExport,
  Sort,
  Resize,
  Group,
  Freeze,
  CommandColumn,
} from "@syncfusion/ej2-react-grids";
import { apiRequest } from "../../../utilities/apiMethod";

export default function GRNGrid({ data = [] }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(false);

  let gridInstance;

  const toolbarClick = (args) => {
    if (gridInstance && args.item.id === "GRN_List_GridID_excelexport") {
      gridInstance.excelExport();
    }
  };

  const handleViewDetails = async (grnNo) => {
    try {
      setLoading(true);
      setModalOpen(true);

      // URL encode the GRN number to handle slashes
      const encodedGrnNo = encodeURIComponent(grnNo);

      const response = await apiRequest(
        "POST",
        `SAPIntegration/saveGrnToSap/${encodedGrnNo}`,
      );

      if (response.IsSuccess && response.Data) {
        setModalData(response.Data);
      } else {
        setModalData({ error: "No data found" });
      }
    } catch (error) {
      console.error("Error fetching GRN details:", error);
      setModalData({ error: "Failed to fetch details" });
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setModalData(null);
    }
  };

  const syncStatusTemplate = (props) => {
    const status = props.SAPSyncStatus;
    const getStatusConfig = () => {
      switch (status) {
        case "Synced":
          return {
            text: "Synced",
            color: "#52c41a",
            bg: "#f6ffed",
            border: "#b7eb8f",
          };
        case "Pending":
          return {
            text: "Pending",
            color: "#ff4d4f",
            bg: "#fff1f0",
            border: "#ffccc7",
          };
        default:
          return {
            text: "Unknown",
            color: "#8c8c8c",
            bg: "#f5f5f5",
            border: "#d9d9d9",
          };
      }
    };
    const config = getStatusConfig();
    return (
      <div style={{ textAlign: "center" }}>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor: config.bg,
            color: config.color,
            border: `1px solid ${config.border}`,
          }}
        >
          {config.text}
        </span>
      </div>
    );
  };

  return (
    <>
      <div
        className={`grid_compact ${isMobile ? "e-bigger" : ""}`}
        style={{
          position: "relative",
          marginTop: "10px",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          padding: "6px",
          marginBottom: "20px",
        }}
      >
        <Row>
          <Col>
            <GridComponent
              ref={(g) => (gridInstance = g)}
              id="GRN_List_GridID"
              dataSource={data}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["ExcelExport", "Search"]}
              allowExcelExport={true}
              toolbarClick={toolbarClick}
              enableAdaptiveUI={isMobile}
              rowRenderingMode={isMobile ? "Vertical" : "Horizontal"}
              gridLines="Both"
              height={"300px"}
              pageSettings={{
                pageSizes: [10, 20, 30, 40, 50, "All"],
                pageCount: 10,
              }}
              dataBound={() => {
                if (gridInstance) {
                  gridInstance.autoFitColumns();
                }
              }}
              queryCellInfo={(args) => {
                if (args.column.headerText === "Actions") {
                  const { GRNSTATUS, SAPSyncStatus } = args.data || {};
                  const syncBtn = args.cell.querySelector(".e-sync-btn");
                  if (syncBtn) {
                    if (GRNSTATUS === "Posted" && SAPSyncStatus === "Pending") {
                      syncBtn.style.display = "inline-block";
                    } else {
                      syncBtn.style.display = "none";
                    }
                  }
                }
              }}
              rowHeight={isMobile ? "" : 32}
              frozenColumns={1}
              commandClick={async (args) => {
                const row = args?.rowData;
                const icon = args?.commandColumn?.buttonOption?.iconCss;
                if (!row?.GRNNO) return;
                // SYNC TO SAP
                if (icon?.includes("e-refresh")) {
                  handleViewDetails(row.GRNNO);
                }
              }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="serialNo"
                  headerText="S No"
                  width="100"
                  textAlign="Center"
                  isPrimaryKey={true}
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="GRNNO"
                  headerText="GRN No"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="GRNDATE"
                  headerText="GRN Date"
                  width="180"
                  textAlign="Center"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="Supplier"
                  headerText="Supplier"
                  width="150"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="SupplierName"
                  headerText="Supplier Name"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="PONo"
                  headerText="PO No"
                  width="180"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="PO_DATE"
                  headerText="PO Date"
                  width="180"
                  textAlign="Left"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="IRNNo"
                  headerText="IRN No"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="IRN_DATE"
                  headerText="IRN Date"
                  width="180"
                  textAlign="Left"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="InvoiceNo"
                  headerText="Invoice No"
                  width="180"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="InvoiceDate"
                  headerText="Invoice Date"
                  width="180"
                  textAlign="Center"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="InvoiceQty"
                  headerText="Invoice Qty"
                  width="180"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="GRNQUANTITY"
                  headerText="GRN Qty"
                  width="180"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="GRNSTATUS"
                  headerText="GRN Status"
                  width="180"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="SAPGRNRefNo"
                  headerText="SAP GRN Ref No"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="SAPSyncStatus"
                  headerText="SAP Sync Status"
                  width="180"
                  textAlign="Center"
                  template={syncStatusTemplate}
                />
                <ColumnDirective
                  field="SAPRemark"
                  headerText="SAP Remark"
                  width="150"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="CreateBy"
                  headerText="Created By"
                  width="150"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="CreatedOn"
                  headerText="Created On"
                  width="220"
                  textAlign="Center"
                  type="datetime"
                  format="dd/MM/yyyy HH:mm"
                />
                <ColumnDirective
                  headerText="Actions"
                  width="120"
                  textAlign="Center"
                  freeze="Right"
                  commands={[
                    {
                      type: "None",
                      buttonOption: {
                        iconCss: "e-icons e-refresh",
                        cssClass: "e-flat e-success e-sync-btn",
                        // title: "Sync to SAP",
                        content: "",
                      },
                      tooltipText: "Sync to SAP",
                    },
                  ]}
                  showInAdaptiveUI={false}
                />
              </ColumnsDirective>
              <Inject
                services={[
                  CommandColumn,
                  Page,
                  Filter,
                  Toolbar,
                  ExcelExport,
                  Sort,
                  Group,
                  Resize,
                  Freeze,
                ]}
              />
            </GridComponent>
          </Col>
        </Row>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>GRN Details</ModalHeader>
        <ModalBody>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
            </div>
          ) : modalData ? (
            modalData.error ? (
              <div
                style={{ textAlign: "center", padding: "20px", color: "red" }}
              >
                {modalData.error}
              </div>
            ) : (
              <div style={{ maxHeight: "500px", overflow: "auto" }}>
                {Array.isArray(modalData) ? (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#f0f0f0",
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        {modalData.length > 0 &&
                          Object.keys(modalData[0]).map((key) => (
                            <th
                              key={key}
                              style={{
                                padding: "8px",
                                textAlign: "left",
                                borderBottom: "2px solid #d9d9d9",
                                fontWeight: "600",
                                color: "#333",
                              }}
                            >
                              {key}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.map((row, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: "1px solid #e8e8e8" }}
                        >
                          {Object.values(row).map((value, i) => (
                            <td
                              key={i}
                              style={{
                                padding: "8px",
                                color: "#666",
                              }}
                            >
                              {value !== null && value !== undefined
                                ? String(value)
                                : "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {Object.entries(modalData).map(([key, value]) => (
                        <tr
                          key={key}
                          style={{ borderBottom: "1px solid #e8e8e8" }}
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontWeight: "600",
                              backgroundColor: "#f5f5f5",
                              width: "40%",
                              color: "#333",
                            }}
                          >
                            {key}
                          </td>
                         
                          <td
                            style={{
                              padding: "8px",
                              color: "#666",
                            }}
                          >
                            {value !== null && value !== undefined ? (
                              typeof value === "object" ? (
                                <pre
                                  style={{
                                    margin: 0,
                                    whiteSpace: "pre-wrap",
                                    background: "#f7f7f7",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    maxHeight: "250px",
                                    overflow: "auto",
                                  }}
                                >
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              ) : (
                                String(value)
                              )
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
}
